/// Port of mobile/lib/ui/pixel/pixel_dropdown.dart.
///
/// Flutter needs an OverlayPortal plus scroll-into-view maths to fit the menu.
/// On the web the menu is an absolutely positioned sibling, so the only carried
/// over behaviours are: the current value is excluded from the list, an
/// outside click closes it, and the chevron flips while open.

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useThemeColors } from '@/settings/handler/settingsStore';
import { PixelContainer } from './PixelContainer';
import { PixelIcon } from './PixelIcon';

const menuGap = 6;
const itemExtent = 40;

export interface PixelDropdownProps<T extends string> {
  value: T;
  items: readonly T[];
  labelOf: (value: T) => ReactNode;
  onChange: (value: T) => void;
  pixelScale?: number;
  radius?: number;
}

export function PixelDropdown<T extends string>({
  value,
  items,
  labelOf,
  onChange,
  pixelScale = 2,
  radius = 3,
}: PixelDropdownProps<T>) {
  const colors = useThemeColors();
  const [isOpen, setIsOpen] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);

  const menuItems = items.filter((item) => item !== value);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: PointerEvent): void => {
      if (!hostRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={hostRef} className="relative w-full">
      <PixelContainer
        radius={radius}
        pixelScale={pixelScale}
        padding="8px 12px"
        backgroundColor={colors.surfaceContainerHighest}
        borderColor={colors.onSurface}
        onClick={() => setIsOpen((open) => !open)}
        className="w-full"
      >
        <div className="flex w-full items-center gap-2">
          <div className="flex flex-1 items-center leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
            {labelOf(value)}
          </div>
          <PixelIcon
            name="chevron-down-2"
            set="pixel_icons"
            size={16}
            color={colors.onSurface}
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 150ms',
            }}
          />
        </div>
      </PixelContainer>

      {isOpen && (
        <div
          className="absolute left-0 z-50 w-full"
          style={{ top: `calc(100% + ${menuGap}px)`, maxHeight: itemExtent * 4 + pixelScale * 2 }}
        >
          <PixelContainer
            radius={radius}
            pixelScale={pixelScale}
            padding="0px"
            backgroundColor={colors.surfaceContainerHighest}
            borderColor={colors.onSurface}
          >
            <ul className="overflow-y-auto" style={{ maxHeight: itemExtent * 4 }}>
              {menuItems.map((item) => (
                <li key={item}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(item);
                      setIsOpen(false);
                    }}
                    className="flex w-full items-center px-3 text-left leading-none"
                    style={{ height: itemExtent, fontSize: 20, color: colors.onSurface, opacity: 0.85 }}
                  >
                    {labelOf(item)}
                  </button>
                </li>
              ))}
            </ul>
          </PixelContainer>
        </div>
      )}
    </div>
  );
}
